// src/utils/windchillCsv.ts — SCE Issues
// Parsing d'un export CSV Windchill PLM → lignes de référentiel SCE_Equipements.
//
// ─── Pourquoi ce module est 100 % pur ───
//
// Aucun appel réseau, aucune dépendance React : tout est testable avec un
// simple fichier de fixtures. C'est volontaire — la difficulté de l'import
// Windchill est dans le PARSING, pas dans l'écriture SharePoint.
//
// ─── Forme réelle de l'export Windchill ───
//
// Colonnes (en-tête tel qu'exporté) :
//   Project Number, Name, Description, Phase, OF, Fourniture,
//   Serial Number, Désignation de recherche, Customer Name, Country,
//   Owner, Expected date of FAT, Expected date of SAT
//
// ⚠️ Le piège central : OF / Fourniture / Serial Number / Désignation sont des
// LISTES NUMÉROTÉES à l'intérieur d'UNE seule cellule, corrélées par leur index.
//
//   OF             = "1. OF-2406-0241\n2. OF-2406-0242"
//   Fourniture     = "1. Hip Flowdrive XXL custom (NVS50193)\n2. Hip ..."
//   Serial Number  = "1. 4618-H\n2. 4619-H"
//
// Il faut donc « dézipper » ces colonnes par index pour reconstituer
// 1 OF ↔ 1 équipement (la règle métier SCE).
//
// ─── Les cas tordus observés dans les exports réels ───
//
//   "1.4275-SC"                      → pas d'espace après le point
//   "4260-S"                         → valeur seule, sans numérotation
//   "1. 4626-SC / 4624-G / 4625-CO"  → plusieurs N° de série pour un seul OF
//   "4562-SC (4561-CO / 4560-CO)"    → N° de série entre parenthèses
//   "3. cancelled : 4659-S"          → ligne annulée
//   "4. 4673-B (ANNULE)"             → ligne annulée (en français)
//   "1."                             → entrée numérotée vide
//   " " / "N/A" / "NA" / "cf. Dossiers" → cellule non renseignée
//   "1245-H-06"                      → N° de série avec indice de révision
//   OF = "1. OF-2212-0236" mais 3 N° de série → projet d'upgrade, désalignement
//
// ─── Périmètre applicatif ───
//
// Sont importés les suffixes présents dans `CATEGORIES_PAR_SUFFIXE`
// (issue.types.ts) : -H, -S, -SC, -B, -SD.
//
// Restent hors périmètre, comptés et affichés dans l'aperçu mais PAS importés
// (ce ne sont pas des rejets) :
//   -CO  corps de colonne   ┐ sous-ensembles d'une colonne -SC, pas des
//   -G   groupe hydraulique ┘ machines déclarables en tant que telles

import {
  CATEGORIES_PAR_SUFFIXE,
  suffixeNumeroSerie,
  type CategorieEquipement,
} from '@/types/issue.types'

// ─── Périmètre : suffixes de N° de série importés ───
//
// La table suffixe → catégorie vit dans `issue.types.ts`
// (`CATEGORIES_PAR_SUFFIXE`), partagée avec `deduireCategorieEquipement()`.
// L'étendre ici seul n'aurait aucun effet côté formulaire.

/** Libellés informatifs des suffixes hors périmètre (pour l'aperçu). */
export const SUFFIXES_HORS_PERIMETRE: Record<string, string> = {
  CO: "Corps de colonne — sous-ensemble d'une colonne -SC",
  G: "Groupe hydraulique — sous-ensemble d'une colonne -SC",
}

// ─── Types publics ───

/** Un équipement candidat à l'import, déjà normalisé. */
export interface LigneEquipement {
  numeroSerie: string
  of: string
  pn: string
  cve: string
  fourniture: string
  designation: string
  chefProjet: string
  /**
   * Client du PROJET (colonne Customer Name, 20/10), le même pour toutes ses
   * machines. Vide si l'export n'a pas la colonne ou qu'elle n'est pas remplie.
   */
  client: string
  categorie: CategorieEquipement
  /**
   * Phase du PROJET (« P4 : MANUFACTURING »), la même pour toutes ses machines.
   * Vide si l'export n'a pas la colonne. N'est pas écrite dans SCE_Equipements :
   * seul le cycle de vie la garde (`utils/cycleVie`, 19/10).
   */
  phase: string
  /** Index (1-based) de la ligne CSV d'origine — pour tracer dans l'aperçu. */
  ligneSource: number
}

/** Une ligne que le parseur n'a pas su exploiter → destinée à SCE_Quarantine. */
export interface RejetImport {
  /** Résumé lisible, écrit dans la colonne Title de SCE_Quarantine. */
  titre: string
  /** Motif du rejet, écrit dans la colonne Raison. */
  raison: string
  /** JSON de la ligne CSV source, écrit dans la colonne LigneBrute. */
  ligneBrute: string
  ligneSource: number
}

/** Un N° de série reconnu mais volontairement non importé. */
export interface HorsPerimetre {
  numeroSerie: string
  suffixe: string
  libelle: string
  ligneSource: number
}

export interface ResultatParsing {
  lignes: LigneEquipement[]
  rejets: RejetImport[]
  horsPerimetre: HorsPerimetre[]
  /** N° de série explicitement marqués annulés / cancelled dans l'export. */
  annulees: { numeroSerie: string; ligneSource: number }[]
  stats: {
    lignesCsv: number
    equipementsRetenus: number
    rejets: number
    horsPerimetre: number
    annulees: number
    doublonsInternes: number
  }
  /** Colonnes attendues manquantes dans l'en-tête (bloquant si non vide). */
  colonnesManquantes: string[]
}

export interface OptionsParsing {
  /**
   * Transforme "xavier.aubrion" → "Xavier Aubrion" et fusionne les comptes
   * numérotés ("lucile.goy1" → "Lucile Goy").
   *
   * ⚠️ Par défaut à false : les lignes déjà présentes dans SCE_Equipements ont
   * été écrites avec la valeur brute par l'ancien flow Power Automate. Activer
   * l'option crée deux libellés différents pour la même personne tant que tout
   * le référentiel n'a pas été réimporté.
   */
  normaliserChefProjet?: boolean
}

// ─── En-têtes attendus ───

const COLONNES_REQUISES = [
  'Project Number',
  'Name',
  'OF',
  'Fourniture',
  'Serial Number',
  'Désignation de recherche',
  'Owner',
] as const

// ─── Décodage du fichier ───

/**
 * Décode le contenu binaire d'un fichier CSV en texte.
 *
 * Windchill et Excel exportent tantôt en UTF-8, tantôt en Windows-1252.
 * On tente UTF-8 d'abord ; si le résultat contient des caractères de
 * remplacement (U+FFFD) ou les séquences de mojibake classiques ("Ã©", "Ã¨"),
 * on rebascule sur Windows-1252.
 *
 * Le BOM UTF-8 éventuel est retiré — sinon la première colonne s'appellerait
 * "﻿Project Number" et ne serait jamais trouvée.
 */
export function decoderFichierCsv(buffer: ArrayBuffer): string {
  const utf8 = new TextDecoder('utf-8').decode(buffer)

  const mojibake = /�|Ã[©¨ªàèé¢]|Â°/.test(utf8)
  const texte = mojibake
    ? new TextDecoder('windows-1252').decode(buffer)
    : utf8

  return texte.charCodeAt(0) === 0xfeff ? texte.slice(1) : texte
}

/**
 * Devine le séparateur de colonnes depuis la ligne d'en-tête.
 * Windchill exporte en virgule, mais un aller-retour par Excel FR produit
 * souvent un point-virgule.
 */
export function detecterSeparateur(texte: string): ',' | ';' | '\t' {
  const premiereLigne = texte.slice(0, texte.indexOf('\n') + 1 || texte.length)
  const candidats: (',' | ';' | '\t')[] = [',', ';', '\t']

  let meilleur: ',' | ';' | '\t' = ','
  let meilleurScore = -1
  for (const sep of candidats) {
    const score = premiereLigne.split(sep).length
    if (score > meilleurScore) {
      meilleurScore = score
      meilleur = sep
    }
  }
  return meilleur
}

// ─── Parseur CSV (RFC 4180) ───

/**
 * Découpe un texte CSV en tableau de lignes de cellules.
 *
 * Implémenté en automate à états plutôt qu'en `split()` : les descriptions
 * Windchill contiennent des retours à la ligne ET des guillemets échappés
 * à l'intérieur des champs quotés, ce qu'un split ne sait pas gérer.
 *
 * Règles couvertes :
 *  - champ quoté pouvant contenir séparateur, \n et "" (guillemet échappé)
 *  - fins de ligne \n et \r\n
 *  - dernière ligne sans saut de ligne final
 */
export function parserCsv(texte: string, separateur: string): string[][] {
  const lignes: string[][] = []
  let cellules: string[] = []
  let cellule = ''
  let dansGuillemets = false

  for (let i = 0; i < texte.length; i += 1) {
    const c = texte[i]

    if (dansGuillemets) {
      if (c === '"') {
        if (texte[i + 1] === '"') {
          // Guillemet échappé ("") → un vrai guillemet dans la valeur
          cellule += '"'
          i += 1
        } else {
          dansGuillemets = false
        }
      } else {
        cellule += c
      }
      continue
    }

    if (c === '"') {
      dansGuillemets = true
    } else if (c === separateur) {
      cellules.push(cellule)
      cellule = ''
    } else if (c === '\n') {
      cellules.push(cellule)
      lignes.push(cellules)
      cellules = []
      cellule = ''
    } else if (c === '\r') {
      // Ignoré : le \n qui suit termine la ligne (CRLF)
    } else {
      cellule += c
    }
  }

  // Dernière cellule / dernière ligne si le fichier ne finit pas par \n
  if (cellule !== '' || cellules.length > 0) {
    cellules.push(cellule)
    lignes.push(cellules)
  }

  return lignes
}

// ─── Helpers de nettoyage ───

const VALEURS_VIDES = new Set([
  '',
  'n/a',
  'na',
  'n.a.',
  '-',
  '?',
  'cf. dossiers',
  'cf dossiers',
])

/** true si la cellule ne porte aucune information exploitable. */
export function estVide(valeur: string | undefined): boolean {
  if (!valeur) return true
  return VALEURS_VIDES.has(valeur.trim().toLowerCase())
}

/** Valeurs de remplissage vues dans la colonne Customer Name (projets de démo, en-tête recopié). */
const CLIENTS_BIDON = new Set(['xx', 'xxx', 'tbd', 'customer name', 'customer', 'client'])

/**
 * Nom du client tel que Windchill l'écrit (« BACHEM », « Corden Pharma US ») :
 * texte libre, casse variable, gardé tel quel — espaces resserrés, valeurs de
 * remplissage écartées. Vide s'il n'y a rien d'exploitable.
 */
export function nettoyerClient(brut: string | undefined): string {
  const t = (brut ?? '').replace(/\s+/g, ' ').trim()
  return estVide(t) || CLIENTS_BIDON.has(t.toLowerCase()) ? '' : t
}

/** true si le texte signale une entrée annulée (anglais ou français). */
export function estAnnule(texte: string): boolean {
  return /\b(cancelled|canceled|annul[ée]e?s?)\b/i.test(texte)
}

/**
 * Éclate une cellule contenant une liste numérotée en Map index → contenu.
 *
 * "1. OF-2203-0111\n2. OF-2204-0123"  → { 1: "OF-2203-0111", 2: "OF-2204-0123" }
 * "1.4275-SC\n2.4278-SC"              → { 1: "4275-SC", 2: "4278-SC" }
 * "4260-S"                            → { 1: "4260-S" }   (pas de numérotation)
 * "N/A"                               → {}                 (vide)
 *
 * Le point d'entrée est une regex multiligne : on repère les préfixes
 * "<n>." en début de ligne, puis on découpe entre deux préfixes successifs.
 */
export function eclaterListeNumerotee(cellule: string): Map<number, string> {
  const resultat = new Map<number, string>()
  if (estVide(cellule)) return resultat

  const regexPrefixe = /^[ \t]*(\d{1,3})[ \t]*\.[ \t]*/gm
  const marqueurs: { index: number; debut: number; finPrefixe: number }[] = []

  for (const m of cellule.matchAll(regexPrefixe)) {
    const numero = Number.parseInt(m[1] ?? '0', 10)
    if (numero > 0 && m.index !== undefined) {
      marqueurs.push({
        index: numero,
        debut: m.index,
        finPrefixe: m.index + m[0].length,
      })
    }
  }

  // Aucune numérotation → la cellule entière est l'entrée n° 1
  if (marqueurs.length === 0) {
    const valeur = cellule.trim()
    if (!estVide(valeur)) resultat.set(1, valeur)
    return resultat
  }

  marqueurs.forEach((marqueur, i) => {
    const fin = marqueurs[i + 1]?.debut ?? cellule.length
    const valeur = cellule.slice(marqueur.finPrefixe, fin).trim()
    // Une entrée peut apparaître deux fois (export incohérent) : le premier gagne
    if (!resultat.has(marqueur.index)) {
      resultat.set(marqueur.index, valeur)
    }
  })

  return resultat
}

/**
 * Extrait tous les N° de série d'un fragment de texte.
 *
 * Accepte : "4618-H", "1245-H-06", "4626-SC / 4624-G", "4562-SC (4561-CO)".
 * La forme est : 3 ou 4 chiffres, tiret, 1 à 3 lettres, puis un suffixe
 * de révision optionnel "-NN".
 */
export function extraireNumerosSerie(fragment: string): string[] {
  const regex = /\b(\d{3,4}-[A-Za-z]{1,3}(?:-\d{1,2})?)\b/g
  const trouves = [...fragment.matchAll(regex)].map((m) =>
    (m[1] ?? '').toUpperCase(),
  )
  return [...new Set(trouves)]
}

/**
 * Extrait le premier OF valide d'un fragment ("1. OF-2406-0241" → "OF-2406-0241").
 *
 * Écritures abîmées rencontrées dans l'export du 17/09, remises au format
 * plutôt que mises en quarantaine : « OF 2201-0244 », « OF2201-0244 », et le
 * numéro seul « 2403-0035 » quand il occupe TOUTE la cellule (ailleurs, deux
 * groupes de 4 chiffres pourraient être autre chose qu'un OF).
 */
export function extraireOf(fragment: string): string {
  const texte = fragment.toUpperCase()
  const m = texte.match(/\bOF\s*-?\s*(\d{4})-(\d{4})\b/)
  if (m) return `OF-${m[1]}-${m[2]}`
  const seul = texte.match(/^\s*(?:\d+\.\s*)?(\d{4})-(\d{4})\s*$/)
  return seul ? `OF-${seul[1]}-${seul[2]}` : ''
}

/** Extrait le premier CVE d'un fragment ("CVE-2206-0001 - PN3317" → "CVE-2206-0001"). */
export function extraireCve(fragment: string): string {
  const m = fragment.toUpperCase().match(/\bCVE-\d{4}-\d{4}\b/)
  return m?.[0] ?? ''
}

/**
 * "xavier.aubrion" → "Xavier Aubrion".
 * Le chiffre final des comptes homonymes ("lucile.goy1") est retiré pour que
 * la même personne n'apparaisse pas deux fois dans le filtre du dashboard.
 */
export function normaliserNomChefProjet(owner: string): string {
  const base = owner.trim().replace(/\d+$/, '')
  if (base === '') return owner.trim()
  return base
    .split(/[._]+/)
    .filter((mot) => mot !== '')
    .map((mot) => mot.charAt(0).toUpperCase() + mot.slice(1).toLowerCase())
    .join(' ')
}

// ─── Analyse complète ───

/**
 * Analyse un export Windchill complet et produit les lignes prêtes à écrire
 * dans SCE_Equipements, plus les rejets destinés à SCE_Quarantine.
 *
 * Le parcours par ligne CSV est le suivant :
 *  1. On éclate les 4 colonnes-listes en Map index → valeur
 *  2. On itère sur l'union des index rencontrés
 *  3. Pour chaque index : on extrait les N° de série, on écarte les annulés,
 *     on sépare le périmètre applicatif (-H / -SC) du reste
 *  4. Un N° de série en périmètre sans OF valide part en quarantaine
 *     (la règle 1 OF = 1 équipement est structurante : sans OF la ligne est
 *     inexploitable côté formulaire)
 *  5. Une ligne CSV sans AUCUN N° de série en périmètre n'est pas un rejet :
 *     c'est du hors-périmètre, comptabilisé mais pas remonté en quarantaine
 *     (sinon la quarantaine se remplit de slurry units et devient illisible)
 */
export function analyserExportWindchill(
  texte: string,
  options: OptionsParsing = {},
): ResultatParsing {
  const separateur = detecterSeparateur(texte)
  const grille = parserCsv(texte, separateur)

  const resultat: ResultatParsing = {
    lignes: [],
    rejets: [],
    horsPerimetre: [],
    annulees: [],
    stats: {
      lignesCsv: 0,
      equipementsRetenus: 0,
      rejets: 0,
      horsPerimetre: 0,
      annulees: 0,
      doublonsInternes: 0,
    },
    colonnesManquantes: [],
  }

  const entete = grille[0]
  if (!entete) {
    resultat.colonnesManquantes = [...COLONNES_REQUISES]
    return resultat
  }

  const colonnes = entete.map((c) => c.trim())
  const indexDe = (nom: string): number => colonnes.indexOf(nom)

  resultat.colonnesManquantes = COLONNES_REQUISES.filter(
    (nom) => indexDe(nom) === -1,
  )
  if (resultat.colonnesManquantes.length > 0) return resultat

  const iPn = indexDe('Project Number')
  const iName = indexDe('Name')
  const iOf = indexDe('OF')
  const iFourniture = indexDe('Fourniture')
  const iSerial = indexDe('Serial Number')
  const iDesignation = indexDe('Désignation de recherche')
  const iOwner = indexDe('Owner')
  // Facultative : un export sans elle reste importable (cycle de vie sans phase).
  const iPhase = indexDe('Phase')
  // Facultative aussi : le client (20/10), pour la recherche et les fiches.
  const iClient = indexDe('Customer Name')

  // Déduplication intra-fichier : un même N° de série peut apparaître dans
  // deux projets (reprise de matériel). Le premier rencontré fait foi.
  const vus = new Set<string>()

  for (let l = 1; l < grille.length; l += 1) {
    const ligne = grille[l]
    if (!ligne) continue

    // Ligne vide résiduelle (fichier finissant par plusieurs sauts de ligne)
    if (ligne.every((c) => c.trim() === '')) continue

    resultat.stats.lignesCsv += 1
    const numeroLigne = l + 1 // 1-based, en-tête comprise — correspond à Excel

    const cellule = (i: number): string => (i >= 0 ? (ligne[i] ?? '') : '')
    const ligneBrute = JSON.stringify(
      Object.fromEntries(colonnes.map((nom, i) => [nom, ligne[i] ?? ''])),
    )

    const pn = estVide(cellule(iPn)) ? '' : cellule(iPn).trim()
    const phase = estVide(cellule(iPhase)) ? '' : cellule(iPhase).replace(/\s+/g, ' ').trim()
    const cve = extraireCve(cellule(iName))
    const client = nettoyerClient(cellule(iClient))
    const ownerBrut = cellule(iOwner).trim()
    const chefProjet =
      options.normaliserChefProjet === true
        ? normaliserNomChefProjet(ownerBrut)
        : ownerBrut

    const mapOf = eclaterListeNumerotee(cellule(iOf))
    const mapFourniture = eclaterListeNumerotee(cellule(iFourniture))
    const mapSerial = eclaterListeNumerotee(cellule(iSerial))
    const mapDesignation = eclaterListeNumerotee(cellule(iDesignation))

    const indices = [
      ...new Set([
        ...mapOf.keys(),
        ...mapFourniture.keys(),
        ...mapSerial.keys(),
        ...mapDesignation.keys(),
      ]),
    ].sort((a, b) => a - b)

    let enPerimetreSurLaLigne = 0

    for (const idx of indices) {
      const fragmentSerial = mapSerial.get(idx) ?? ''
      const fragmentFourniture = mapFourniture.get(idx) ?? ''
      const fragmentOf = mapOf.get(idx) ?? ''

      if (estVide(fragmentSerial)) continue

      // Annulation : marquée tantôt sur le N° de série, tantôt sur la fourniture
      if (estAnnule(fragmentSerial) || estAnnule(fragmentFourniture)) {
        for (const ns of extraireNumerosSerie(fragmentSerial)) {
          resultat.annulees.push({ numeroSerie: ns, ligneSource: numeroLigne })
        }
        continue
      }

      const numerosSerie = extraireNumerosSerie(fragmentSerial)
      if (numerosSerie.length === 0) continue

      // On trie d'abord périmètre / hors périmètre : la décision d'import
      // dépend du NOMBRE d'équipements en périmètre sur cet index.
      const enPerimetre: { numeroSerie: string; categorie: CategorieEquipement }[] = []
      for (const numeroSerie of numerosSerie) {
        const suffixe = suffixeNumeroSerie(numeroSerie)
        const categorie = CATEGORIES_PAR_SUFFIXE[suffixe]
        if (categorie) {
          enPerimetre.push({ numeroSerie, categorie })
        } else {
          resultat.horsPerimetre.push({
            numeroSerie,
            suffixe,
            libelle: SUFFIXES_HORS_PERIMETRE[suffixe] ?? 'Suffixe inconnu',
            ligneSource: numeroLigne,
          })
        }
      }

      enPerimetreSurLaLigne += enPerimetre.length
      if (enPerimetre.length === 0) continue

      const of = extraireOf(fragmentOf)
      if (of === '') {
        for (const { numeroSerie } of enPerimetre) {
          resultat.rejets.push({
            titre: `${numeroSerie} — OF manquant`,
            raison:
              `N° de série ${numeroSerie} en périmètre mais aucun OF valide ` +
              `à l'index ${idx} (valeur lue : "${fragmentOf.trim() || '(vide)'}"). ` +
              `La règle 1 OF = 1 équipement interdit l'import sans OF.`,
            ligneBrute,
            ligneSource: numeroLigne,
          })
        }
        continue
      }

      // Deux équipements en périmètre sous le même index partageraient le même
      // OF. On refuse : `getEquipementByOF` est écrit pour une relation 1-1 et
      // le formulaire résoudrait l'OF sur un équipement arbitraire.
      if (enPerimetre.length > 1) {
        const liste = enPerimetre.map((e) => e.numeroSerie).join(', ')
        for (const { numeroSerie } of enPerimetre) {
          resultat.rejets.push({
            titre: `${numeroSerie} — OF ${of} partagé`,
            raison:
              `${enPerimetre.length} équipements en périmètre (${liste}) sont ` +
              `rattachés au même OF ${of} à l'index ${idx}. La règle ` +
              `1 OF = 1 équipement impose de corriger l'export Windchill.`,
            ligneBrute,
            ligneSource: numeroLigne,
          })
        }
        continue
      }

      const seul = enPerimetre[0]
      if (!seul) continue

      const cle = seul.numeroSerie.toUpperCase()
      if (vus.has(cle)) {
        resultat.stats.doublonsInternes += 1
        continue
      }
      vus.add(cle)

      resultat.lignes.push({
        numeroSerie: seul.numeroSerie,
        of,
        pn,
        cve,
        fourniture: fragmentFourniture.trim(),
        designation: (mapDesignation.get(idx) ?? '').trim(),
        chefProjet,
        client,
        categorie: seul.categorie,
        phase,
        ligneSource: numeroLigne,
      })
    }

    // Désalignement structurel : des OF sont déclarés, des N° de série aussi,
    // mais aucun n'est exploitable → on trace la ligne pour investigation.
    if (enPerimetreSurLaLigne === 0 && mapOf.size > 0 && mapSerial.size > 0) {
      const totalSerials = [...mapSerial.values()].flatMap((f) =>
        extraireNumerosSerie(f),
      ).length
      if (totalSerials > 0) {
        resultat.rejets.push({
          titre: `${pn || cve || 'Ligne ' + numeroLigne} — hors périmètre applicatif`,
          raison:
            `Désalignement OF/Serial : 0 en périmètre ` +
            `(${Object.keys(CATEGORIES_PAR_SUFFIXE).map((x) => '-' + x).join('/')}), ` +
            `${mapOf.size} OF, ${totalSerials} N° de série lus.`,
          ligneBrute,
          ligneSource: numeroLigne,
        })
      }
    }
  }

  resultat.stats.equipementsRetenus = resultat.lignes.length
  resultat.stats.rejets = resultat.rejets.length
  resultat.stats.horsPerimetre = resultat.horsPerimetre.length
  resultat.stats.annulees = resultat.annulees.length

  return resultat
}
